import React from 'react';
import {
  Sparkles,
  Compass,
  TrendingUp,
  Cpu,
  ShieldCheck,
  Check,
  Star,
  Award,
  Zap,
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  HelpCircle,
  Users,
  Image as ImageIcon,
  Anchor,
  Ship,
  Calendar,
  Layers,
  Layout,
  FileText,
  Clock,
  Briefcase,
  DollarSign,
  Heart,
  Globe,
} from 'lucide-react';
import { CurrencyCode, PricePeriod, StructuredPrice } from '../types';

/**
 * Formats structured or string price cleanly for display
 * Examples:
 * - { amount: 690, currency: 'TRY', period: 'month' } -> "690 ₺ / ay"
 * - { amount: 18000, currency: 'TRY', period: 'year' } -> "18.000 ₺ / yıl"
 * - { amount: 2500, currency: 'EUR', period: 'day' } -> "2.500 € / gün"
 * - { amount: 35000, currency: 'USD', period: 'one_time' } -> "$35.000 (Tek Seferlik)"
 */
export function formatPriceDisplay(plan: {
  price?: string;
  priceAmount?: number;
  priceCurrency?: CurrencyCode;
  pricePeriod?: PricePeriod;
  customPriceText?: string;
}): {
  displayAmount: string;
  displayPeriod: string;
  fullFormatted: string;
  currencySymbol: string;
} {
  if (plan.customPriceText && plan.customPriceText.trim().length > 0) {
    return {
      displayAmount: plan.customPriceText,
      displayPeriod: '',
      fullFormatted: plan.customPriceText,
      currencySymbol: '',
    };
  }

  const currencySymbols: Record<CurrencyCode, string> = {
    TRY: '₺',
    EUR: '€',
    USD: '$',
    GBP: '£',
  };

  const periodLabels: Record<PricePeriod, string> = {
    month: '/ ay',
    year: '/ yıl',
    day: '/ gün',
    week: '/ hafta',
    one_time: 'Tek Seferlik',
    custom: '',
  };

  const currency = plan.priceCurrency || 'TRY';
  const symbol = currencySymbols[currency] || '₺';

  if (typeof plan.priceAmount === 'number' && !isNaN(plan.priceAmount)) {
    const formattedNum = new Intl.NumberFormat('tr-TR').format(plan.priceAmount);
    const period = plan.pricePeriod ? periodLabels[plan.pricePeriod] || '' : '/ ay';
    const displayAmount = currency === 'USD' || currency === 'GBP' ? `${symbol}${formattedNum}` : `${formattedNum} ${symbol}`;
    
    return {
      displayAmount,
      displayPeriod: period,
      fullFormatted: `${displayAmount} ${period}`.trim(),
      currencySymbol: symbol,
    };
  }

  // Fallback to legacy string price if provided
  if (plan.price && plan.price.trim().length > 0) {
    return {
      displayAmount: plan.price,
      displayPeriod: '',
      fullFormatted: plan.price,
      currencySymbol: '',
    };
  }

  return {
    displayAmount: 'Teklif Alın',
    displayPeriod: '',
    fullFormatted: 'Teklif Alın',
    currencySymbol: '',
  };
}

/**
 * Builds safe URL based on button link type
 */
export function buildActionUrl(
  linkType: 'page' | 'url' | 'whatsapp' | 'phone' | 'email' = 'page',
  linkValue: string = '',
  extra?: {
    whatsappPhone?: string;
    whatsappMessage?: string;
    phoneNumber?: string;
    emailAddress?: string;
  }
): string {
  switch (linkType) {
    case 'whatsapp': {
      const rawPhone = extra?.whatsappPhone || linkValue || '';
      const cleanPhone = rawPhone.replace(/[^0-9]/g, '');
      const msg = encodeURIComponent(extra?.whatsappMessage || 'Merhaba, web siteniz üzerinden bilgi almak istiyorum.');
      return cleanPhone ? `https://wa.me/${cleanPhone}?text=${msg}` : '#';
    }
    case 'phone': {
      const rawPhone = extra?.phoneNumber || linkValue || '';
      const cleanPhone = rawPhone.replace(/[^0-9+]/g, '');
      return cleanPhone ? `tel:${cleanPhone}` : '#';
    }
    case 'email': {
      const email = extra?.emailAddress || linkValue || '';
      return email ? `mailto:${email}` : '#';
    }
    case 'url': {
      if (!linkValue) return '#';
      if (linkValue.startsWith('http://') || linkValue.startsWith('https://')) {
        return linkValue;
      }
      return `https://${linkValue}`;
    }
    case 'page':
    default: {
      if (!linkValue) return '/';
      return linkValue.startsWith('/') ? linkValue : `/${linkValue}`;
    }
  }
}

/**
 * Available Lucide icon map for services & features
 */
export const AVAILABLE_ICONS: Record<string, { label: string; icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }> }> = {
  Compass: { label: 'Pusula / Keşif', icon: Compass },
  TrendingUp: { label: 'Büyüme / Finans', icon: TrendingUp },
  Cpu: { label: 'Teknoloji / AI', icon: Cpu },
  ShieldCheck: { label: 'Güvenlik / Kalite', icon: ShieldCheck },
  Zap: { label: 'Hız / Enerji', icon: Zap },
  Star: { label: 'Yıldız / Öne Çıkan', icon: Star },
  Award: { label: 'Ödül / Başarı', icon: Award },
  Check: { label: 'Onay / Doğrulama', icon: Check },
  Users: { label: 'Topluluk / Ekip', icon: Users },
  Briefcase: { label: 'İş / Portföy', icon: Briefcase },
  DollarSign: { label: 'Fiyat / Tasarruf', icon: DollarSign },
  Heart: { label: 'Müşteri Memnuniyeti', icon: Heart },
  Globe: { label: 'Global / Çevrimiçi', icon: Globe },
  Anchor: { label: 'Çapa / Marin', icon: Anchor },
  Ship: { label: 'Yat / Tekne', icon: Ship },
  Clock: { label: 'Zaman / 7-24', icon: Clock },
  Sparkles: { label: 'Özel / Lüks', icon: Sparkles },
};

export function getIconComponent(iconName?: string) {
  if (iconName && AVAILABLE_ICONS[iconName]) {
    return AVAILABLE_ICONS[iconName].icon;
  }
  return Compass;
}
